import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Store, Send, Plus, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, imgUrl } from "@/lib/api";
import { PageHeader, Pager, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AgentProducts() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [editFor, setEditFor] = useState(null);
  const [prices, setPrices] = useState({});
  const [channels] = useState([]);

  const EMPTY_OWN = { name: "", brand: "", category: "", short_description: "", description: "", images: [], mrp: "", cost_price: "", selling_price: "", gst: 18, stock: 0, weight: 0.5 };
  const [ownOpen, setOwnOpen] = useState(false);
  const [ownEdit, setOwnEdit] = useState(null);
  const [ownForm, setOwnForm] = useState(EMPTY_OWN);

  const { data, isLoading } = useQuery({
    queryKey: ["agent-products", page, search],
    queryFn: async () => (await api.get(`/agent/products?page=${page}&limit=12&search=${encodeURIComponent(search)}`)).data,
  });
  const { data: ownData } = useQuery({
    queryKey: ["own-products"],
    queryFn: async () => (await api.get("/agent/own-products?limit=50")).data,
  });

  const ownPayload = () => ({
    name: ownForm.name, brand: ownForm.brand, category: ownForm.category,
    short_description: ownForm.short_description, description: ownForm.description,
    images: ownForm.images, mrp: Number(ownForm.mrp) || 0, cost_price: Number(ownForm.cost_price) || 0,
    selling_price: Number(ownForm.selling_price) || 0, gst: Number(ownForm.gst) || 18,
    weight: Number(ownForm.weight) || 0.5, stock: Number(ownForm.stock) || 0,
  });
  const saveOwn = useMutation({
    mutationFn: async () => (ownEdit ? api.put(`/agent/own-products/${ownEdit.id}`, ownPayload()) : api.post("/agent/own-products", ownPayload())),
    onSuccess: () => { toast.success(ownEdit ? "Product updated" : "Product submitted — live after admin approval"); setOwnOpen(false); setOwnEdit(null); setOwnForm(EMPTY_OWN); qc.invalidateQueries({ queryKey: ["own-products"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const ownToStore = useMutation({
    mutationFn: async (pid) => api.post(`/agent/own-products/${pid}/to-store`, {}),
    onSuccess: () => { toast.success("Added to your store"); qc.invalidateQueries({ queryKey: ["own-products"] }); qc.invalidateQueries({ queryKey: ["my-store"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const ownMp = useMutation({
    mutationFn: async ({ pid, channel, price }) => api.post("/agent/marketplace/select", { product_id: pid, marketplace: channel, suggested_price: Number(price) }),
    onSuccess: (d, { channel }) => { toast.success(d.data?.already ? "Already submitted" : `Sent for admin review (${channel})`); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const ownStock = useMutation({
    mutationFn: async ({ pid, delta }) => api.post(`/agent/own-products/${pid}/stock`, { delta }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["own-products"] }),
    onError: (e) => toast.error(errMsg(e)),
  });
  const uploadOwnImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const d = (await api.post("/agent/uploads", fd)).data;
      setOwnForm((f) => ({ ...f, images: [...f.images, d.url].slice(0, 5) }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openEdit = (p) => {
    setEditFor(p);
    setPrices({
      price_store: p.assignment?.price_store ?? p.suggested_price,
      price_amazon: p.assignment?.price_amazon ?? p.suggested_price,
      price_flipkart: p.assignment?.price_flipkart ?? p.suggested_price,
    });
  };

  const savePricing = useMutation({
    mutationFn: async () => api.put(`/agent/products/${editFor.id}/pricing`, { ...prices, channels }),
    onSuccess: () => { toast.success("Pricing updated"); qc.invalidateQueries({ queryKey: ["agent-products"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const addToStore = useMutation({
    mutationFn: async ({ pid, price }) => api.post("/agent/store/products", { product_id: pid, price }),
    onSuccess: () => { toast.success("Added to your store"); qc.invalidateQueries({ queryKey: ["agent-products"] }); qc.invalidateQueries({ queryKey: ["my-store"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const mpSelect = useMutation({
    mutationFn: async ({ channel }) => (await api.post("/agent/marketplace/select", {
      product_id: editFor.id, marketplace: channel, suggested_price: Number(prices[`price_${channel}`]),
    })).data,
    onSuccess: (d, { channel }) => { toast.success(d.already ? "Already submitted" : `Request sent to admin for ${channel === "amazon" ? "Amazon" : "Flipkart"} review`); qc.invalidateQueries({ queryKey: ["agent-products"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const profit = (p) => (prices.price_store ?? p.suggested_price) - p.agent_cost;

  return (
    <div data-testid="agent-products-page">
      <PageHeader title="My Products" sub="Set your price per channel — Amazon, Flipkart and store prices are independent." testid="agent-products-title" />

      <div className="mb-5 rounded-xl border border-slate-800 bg-[#111827] p-4" data-testid="own-products-section">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-100">My Own Products <span className="ml-1 rounded bg-purple-500/15 px-1.5 py-0.5 text-[9px] font-bold text-purple-400">MY OWN PRODUCT</span></h3>
            <p className="text-[11px] text-slate-500">Products you own and stock yourself — same inventory everywhere (store, Amazon, Flipkart, your website). Live after admin approval.</p>
          </div>
          <Button data-testid="add-own-product-btn" size="sm" onClick={() => { setOwnEdit(null); setOwnForm(EMPTY_OWN); setOwnOpen(true); }} className="bg-orange-500 text-white hover:bg-orange-600"><Plus size={13} /> Add Own Product</Button>
        </div>
        {ownData?.items?.length > 0 && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {ownData.items.map((p) => (
              <div key={p.id} data-testid={`own-product-${p.sku}`} className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
                <div className="flex gap-2.5">
                  {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <div className="h-10 w-10 rounded-lg bg-slate-800" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-slate-100">{p.name}</p>
                    <p className="font-mono text-[9px] text-slate-500">{p.sku}</p>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {p.approval_status === "PENDING" && <span data-testid={`own-pending-${p.sku}`} className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold text-amber-400">PENDING APPROVAL</span>}
                      {p.approval_status === "APPROVED" && <span data-testid={`own-approved-${p.sku}`} className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">APPROVED</span>}
                      {p.approval_status === "REJECTED" && <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-bold text-rose-400">REJECTED</span>}
                      {p.approval_status === "SUSPENDED" && <span className="rounded bg-slate-500/15 px-1.5 py-0.5 text-[9px] font-bold text-slate-400">SUSPENDED</span>}
                      {p.available <= 0 ? <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-bold text-rose-400">OUT OF STOCK</span> : <span className="rounded bg-slate-700/50 px-1.5 py-0.5 text-[9px] text-slate-400">Stock {p.available}</span>}
                    </div>
                  </div>
                </div>
                {p.approval_status === "REJECTED" && p.reject_reason && <p data-testid={`own-reject-reason-${p.sku}`} className="mt-1.5 rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[10px] text-rose-300">{p.reject_reason}</p>}
                <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400">
                  <span>Cost {fmtINR(p.agent_cost)} · Sell {fmtINR(p.suggested_price)}</span>
                  <span className="flex items-center gap-1">
                    <button data-testid={`stock-minus-${p.sku}`} onClick={() => ownStock.mutate({ pid: p.id, delta: -1 })} className="rounded border border-slate-700 px-1.5 py-0.5 hover:bg-slate-800">−</button>
                    <button data-testid={`stock-plus-${p.sku}`} onClick={() => ownStock.mutate({ pid: p.id, delta: 1 })} className="rounded border border-slate-700 px-1.5 py-0.5 hover:bg-slate-800">+</button>
                  </span>
                </div>
                <div className="mt-2 flex gap-1.5">
                  <Button data-testid={`own-edit-${p.sku}`} size="sm" variant="outline"
                    onClick={() => { setOwnEdit(p); setOwnForm({ name: p.name, brand: p.brand || "", category: p.category || "", short_description: p.short_description || "", description: p.description || "", images: p.images || [], mrp: p.mrp, cost_price: p.agent_cost, selling_price: p.suggested_price, gst: p.gst, stock: p.stock, weight: p.weight }); setOwnOpen(true); }}
                    className="h-7 flex-1 border-slate-700 text-[10px] text-slate-300 hover:bg-slate-800">Edit</Button>
                  <Button data-testid={`own-to-store-${p.sku}`} size="sm" variant="outline" disabled={p.approval_status !== "APPROVED"}
                    onClick={() => ownToStore.mutate(p.id)} className="h-7 flex-1 border-slate-700 text-[10px] text-slate-300 hover:bg-slate-800"><Store size={10} /> Store</Button>
                  <Button data-testid={`own-amazon-${p.sku}`} size="sm" variant="outline" disabled={p.approval_status !== "APPROVED"}
                    onClick={() => ownMp.mutate({ pid: p.id, channel: "amazon", price: p.suggested_price })} className="h-7 flex-1 border-slate-700 text-[10px] text-slate-300 hover:bg-slate-800"><Send size={10} /> Amazon</Button>
                  <Button data-testid={`own-flipkart-${p.sku}`} size="sm" variant="outline" disabled={p.approval_status !== "APPROVED"}
                    onClick={() => ownMp.mutate({ pid: p.id, channel: "flipkart", price: p.suggested_price })} className="h-7 flex-1 border-slate-700 text-[10px] text-slate-300 hover:bg-slate-800"><Send size={10} /> Flipkart</Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <Dialog open={ownOpen} onOpenChange={setOwnOpen}>
          <DialogContent className="max-w-lg border-slate-800 bg-[#111827] text-slate-100">
            <DialogHeader><DialogTitle>{ownEdit ? "Edit own product" : "Add your own product"}</DialogTitle></DialogHeader>
            <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
              <div><Label className="text-xs text-slate-400">Product name *</Label>
                <Input data-testid="own-name-input" value={ownForm.name} onChange={(e) => setOwnForm({ ...ownForm, name: e.target.value })} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-400">Brand</Label>
                  <Input data-testid="own-brand-input" value={ownForm.brand} onChange={(e) => setOwnForm({ ...ownForm, brand: e.target.value })} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
                <div><Label className="text-xs text-slate-400">Category</Label>
                  <Input data-testid="own-category-input" value={ownForm.category} onChange={(e) => setOwnForm({ ...ownForm, category: e.target.value })} placeholder="e.g. Kitchen" className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
                <div><Label className="text-xs text-slate-400">Your cost ₹ *</Label>
                  <Input data-testid="own-cost-input" type="number" value={ownForm.cost_price} onChange={(e) => setOwnForm({ ...ownForm, cost_price: e.target.value })} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
                <div><Label className="text-xs text-slate-400">Selling price ₹ *</Label>
                  <Input data-testid="own-price-input" type="number" value={ownForm.selling_price} onChange={(e) => setOwnForm({ ...ownForm, selling_price: e.target.value })} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
                <div><Label className="text-xs text-slate-400">MRP ₹</Label>
                  <Input data-testid="own-mrp-input" type="number" value={ownForm.mrp} onChange={(e) => setOwnForm({ ...ownForm, mrp: e.target.value })} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
                <div><Label className="text-xs text-slate-400">GST %</Label>
                  <Input data-testid="own-gst-input" type="number" value={ownForm.gst} onChange={(e) => setOwnForm({ ...ownForm, gst: e.target.value })} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
                <div><Label className="text-xs text-slate-400">Stock quantity</Label>
                  <Input data-testid="own-stock-input" type="number" value={ownForm.stock} onChange={(e) => setOwnForm({ ...ownForm, stock: e.target.value })} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
                <div><Label className="text-xs text-slate-400">Weight (kg)</Label>
                  <Input data-testid="own-weight-input" type="number" step="0.1" value={ownForm.weight} onChange={(e) => setOwnForm({ ...ownForm, weight: e.target.value })} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
              </div>
              <div><Label className="text-xs text-slate-400">Short description</Label>
                <Input data-testid="own-short-desc-input" value={ownForm.short_description} onChange={(e) => setOwnForm({ ...ownForm, short_description: e.target.value })} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>
              <div>
                <Label className="text-xs text-slate-400">Images (JPG/PNG/WEBP, max 5MB each, up to 5)</Label>
                <input data-testid="own-image-input" type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadOwnImage(e.target.files[0])}
                  className="mt-1 block w-full text-xs text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-1.5 file:text-xs file:text-slate-200" />
                {ownForm.images.length > 0 && (
                  <div className="mt-2 flex gap-2">{ownForm.images.map((u) => <img key={u} src={imgUrl(u)} alt="" className="h-12 w-12 rounded-lg object-cover" />)}</div>
                )}
              </div>
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[10px] text-amber-300" data-testid="own-approval-note">
                New own products are reviewed by DropPartner admin before they can be sold or listed on marketplaces.
              </p>
              <Button data-testid="own-save-btn" disabled={saveOwn.isPending || !ownForm.name || !ownForm.selling_price}
                onClick={() => saveOwn.mutate()} className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">
                {saveOwn.isPending ? "Saving…" : ownEdit ? "Save changes" : "Submit for approval"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input data-testid="agent-product-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search your products…" className="border-slate-700 bg-[#111827] pl-9 text-slate-100 placeholder:text-slate-600" />
      </div>

      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : !data?.items?.length ? (
        <EmptyState title="No products assigned yet" sub="Your admin assigns products to you — they'll appear here." />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {data.items.map((p) => (
            <div key={p.id} data-testid={`agent-product-${p.sku}`}
              className="group overflow-hidden rounded-xl border border-slate-800 bg-[#111827] transition-colors hover:border-slate-700">
              <div className="relative h-36 overflow-hidden bg-slate-900">
                {p.images?.[0] && <img src={imgUrl(p.images[0])} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                {p.available <= 10 && <span className="absolute left-2 top-2 rounded bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold text-black">LOW STOCK</span>}
              </div>
              <div className="p-3.5">
                <p className="truncate text-sm font-medium text-slate-100">{p.name}</p>
                <p className="font-mono text-[10px] text-slate-500">{p.sku} · {p.available} in stock</p>
                <div className="mt-2 grid grid-cols-3 gap-1 text-center">
                  <div className="rounded bg-slate-900/80 py-1">
                    <p className="text-[9px] uppercase text-slate-500">Cost</p>
                    <p className="text-[11px] font-semibold text-slate-200">{fmtINR(p.agent_cost)}</p>
                  </div>
                  <div className="rounded bg-slate-900/80 py-1">
                    <p className="text-[9px] uppercase text-slate-500">Suggested</p>
                    <p className="text-[11px] font-semibold text-slate-200">{fmtINR(p.suggested_price)}</p>
                  </div>
                  <div className="rounded bg-emerald-500/10 py-1">
                    <p className="text-[9px] uppercase text-slate-500">Profit</p>
                    <p className="text-[11px] font-semibold text-emerald-400">{fmtINR((p.assignment?.price_store ?? p.suggested_price) - p.agent_cost)}</p>
                  </div>
                </div>
                <div className="mt-3 flex gap-1.5">
                  <Button data-testid={`price-product-${p.sku}`} size="sm" onClick={() => openEdit(p)}
                    className="flex-1 bg-orange-500 text-xs text-white hover:bg-orange-600">
                    <Send size={11} /> Sell
                  </Button>
                  <Button data-testid={`ai-ad-${p.sku}`} size="sm" onClick={() => navigate(`/agent/ai-studio?product=${p.id}`)}
                    className="bg-violet-600 text-xs text-white hover:bg-violet-500" title="Create AI ad">
                    <Sparkles size={11} />
                  </Button>
                  {data.has_store && !p.in_store && (
                    <Button data-testid={`add-to-store-${p.sku}`} size="sm" variant="outline" onClick={() => openEdit(p)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"><Plus size={12} /></Button>
                  )}
                  {p.in_store && (
                    <span data-testid={`in-store-${p.sku}`} className="flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 text-[10px] text-emerald-400">
                      <Check size={10} /> In store
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {data && <Pager page={data.page} pages={data.pages} onPage={setPage} />}

      <Dialog open={!!editFor} onOpenChange={() => setEditFor(null)}>
        <DialogContent className="max-w-lg border-slate-800 bg-[#111827] text-slate-100">
          {editFor && (
            <>
              <DialogHeader>
                <DialogTitle className="text-base">{editFor.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-xs">
                <p className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-slate-400">
                  Cost to you: <b className="text-slate-200">{fmtINR(editFor.agent_cost)}</b> · Minimum selling price: <b className="text-amber-400">{fmtINR(editFor.min_price)}</b> ·
                  Est. profit at store price: <b className="text-emerald-400">{fmtINR(profit(editFor))}</b>
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {[["price_store", "Own Store"], ["price_amazon", "Amazon"], ["price_flipkart", "Flipkart"]].map(([k, label]) => (
                    <div key={k}>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label} ₹</Label>
                      <Input data-testid={`pricing-${k}`} type="number" value={prices[k] ?? ""}
                        onChange={(e) => setPrices({ ...prices, [k]: Number(e.target.value) })}
                        className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                    </div>
                  ))}
                </div>
                <div>
                  <Label className="mb-2 block text-[10px] uppercase tracking-wider text-slate-500">Sell on channels</Label>
                  <div className="space-y-2">
                    <div data-testid="sell-channel-store" className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                      <div>
                        <p className="text-xs font-semibold text-slate-200">Own Store {data?.store?.name ? <span className="font-normal text-slate-500">· {data.store.name}</span> : ""}</p>
                        <p className="text-[10px] text-slate-500">Appears on your storefront at {fmtINR(prices.price_store || 0)} — master product data auto-fills, no re-entry needed.</p>
                      </div>
                      {editFor.in_store ? (
                        <span data-testid="sell-store-instore" className="flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[10px] text-emerald-400"><Check size={10} /> In store — Save pricing to update</span>
                      ) : data?.has_store ? (
                        <Button data-testid="sell-add-to-store" size="sm" onClick={() => addToStore.mutate({ pid: editFor.id, price: prices.price_store })} disabled={addToStore.isPending}
                          className="bg-emerald-600 text-[11px] font-semibold text-white hover:bg-emerald-500"><Plus size={11} /> Add to Store</Button>
                      ) : (
                        <a href="/agent/store" data-testid="sell-setup-store" className="text-[10px] font-semibold text-orange-400 hover:underline">Set up your store first</a>
                      )}
                    </div>
                    {[["amazon", "Amazon"], ["flipkart", "Flipkart"]].map(([ch, label]) => {
                      const sel = editFor.mp_selections?.[ch];
                      return (
                        <div key={ch} data-testid={`sell-channel-${ch}`} className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-xs font-semibold text-slate-200">{label}</p>
                              <p className="text-[10px] text-slate-500">Price {fmtINR(prices[`price_${ch}`] || 0)} · admin reviews & publishes.</p>
                            </div>
                            {sel ? (
                              <StatusBadge status={sel.status} />
                            ) : (
                              <Button data-testid={`sell-submit-${ch}`} size="sm" onClick={() => mpSelect.mutate({ channel: ch })} disabled={mpSelect.isPending}
                                className="bg-[#1D5BD8] text-[11px] font-semibold text-white hover:bg-[#1a4fc0]"><Send size={10} /> Submit request</Button>
                            )}
                          </div>
                          {sel?.status === "REJECTED" && sel.review_note && (
                            <p data-testid={`sell-reject-reason-${ch}`} className="mt-1.5 rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[10px] text-rose-300">Rejected: {sel.review_note}</p>
                          )}
                          {["REJECTED", "FAILED"].includes(sel?.status) && (
                            <Button data-testid={`sell-resubmit-${ch}`} size="sm" variant="outline" onClick={() => mpSelect.mutate({ channel: ch })} disabled={mpSelect.isPending}
                              className="mt-1.5 h-6 border-slate-600 px-2 text-[10px] text-slate-300 hover:bg-slate-800">Re-submit request</Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="save-pricing-btn" onClick={() => savePricing.mutate()} disabled={savePricing.isPending}
                    className="flex-1 bg-orange-500 font-semibold text-white hover:bg-orange-600">
                    {savePricing.isPending ? "Saving…" : "Save pricing"}
                  </Button>
                  <p data-testid="marketplace-via-selection-note" className="flex-1 rounded-lg border border-slate-700 px-3 py-2 text-center text-[10px] text-slate-500">
                    Marketplace publishing is done by admin — select this product in <a href="/agent/marketplace" className="font-semibold text-orange-400 hover:underline">My Marketplace</a>
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
